'use strict';


import {CSS2DObject} from '../../../libs/CSS2DRenderer';
import {Box3, Vector3} from 'three';
import {BaseElement} from '../../interfaces/BaseElement';
import {
    threeJsVariables,
    GSAPAnimationMixer,
    Buildings,
    settingsData,
    projectMode,
    isMobile,
} from '../../../settings';
import gsap from 'gsap';
import {getBuildingLabel} from "../../components/building-switch/buildingLabels";
import {ViewMode} from "../../../common";
import {enterToTheBuildingWithMaxAvailableApartments} from "../../components/building-switch/building_switch";
import {getCurrentCameraDistance} from "../../controls/orbitControls";

class CSS2DCluster extends CSS2DObject {
    constructor(element = document.createElement('div'), camera) {
        super(element);

        this.isCSS2DCluster = true;
        this.distance = 100;
        this.camera = camera;
        this.currentDistance = 0;
        this.update = true;
        this.preventUpdate = false;
        this._camera = new Vector3();
        this._position = new Vector3();
    }

    add(object) {
        object.userData._position = new Vector3().copy(object.position);
        super.add(object);
        this.updateClusterPosition();
    }

    updateClusterPosition() {
        const box = new Box3();
        this.children.forEach(object => {
            const vector = new Vector3().copy(object.userData._position);
            // vector.applyMatrix4(this.matrixWorld);
            box.expandByPoint(vector);
        });
        const center = new Vector3();
        const size = new Vector3();
        box.getCenter(center);
        box.getSize(size);

        this.position.set(center.x, center.y + size.y / 2, center.z);
        const offset = new Vector3().sub(this.position);
        this.children.forEach(object => {
            const position = new Vector3().copy(object.userData._position).add(offset);
            object.position.copy(position)
        });
    }

    split() {
        this.visible = false;
        this.children.forEach(object => object.visible = true);
        // if (this.children.length === 1) {
        //     this.preventUpdate = true;
        // }
    }

    merge() {
        this.children.forEach(object => {
            object.visible = false;
            object.element.remove();
        });
        this.visible = true;
    }

    tick() {
        if (!this.update || this.preventUpdate) return;
       
        this._camera.copy(this.camera.position).setY(0);
        this._position.copy(this.position).setY(0);
        this.currentDistance = this._position.distanceTo(this._camera);
        if (this.currentDistance > this.distance && !this.visible) {
            this.merge();
        }
        // if (this.currentDistance <= this.distance && this.visible) {
        //     this.split();
        // } else if (this.currentDistance > this.distance && !this.visible) {
        //     this.merge();
        // }
    }
}


export class Cluster extends CSS2DCluster {
    static scope = [];
    static active = config.features.cluster?.active;
    static buildingLabelMode = config.features.cluster?.buildingLabelMode;

    constructor(lot) {

        const html = new BaseElement()
            .addClass('building-cluster')
            .addAttribute('id', lot);

        if (Cluster.buildingLabelMode) {
            const hideClickHere = config.threejs.label.hideClickHere;
            const colors = config.threejs.label.colors || {};
            const building = Buildings.getBuildingsArray()[0];
            const labelBGColor = Boolean(building.info.color)
                ? building.info.color
                : undefined;

            html
                .addClass('building-labels')
                .removeClass('building-cluster')
                .addAttribute('data-attribute', lot)
                .event('click', async (e) => {
                    if (!e.target.closest('.building-labels_wrapper'))
                        return;

                    const wrapper = e.target.closest('.building-labels_wrapper');
                    const clickedLabelDataName = wrapper.getAttribute('data-name');
                    const clickedLabelBuilding = Buildings.getBuildingsArray().find(building => building.info.buildingLot === clickedLabelDataName);
                    const clickedBuilding = Buildings.getBuilding(clickedLabelBuilding.name);
                    await projectMode.Cylinder.enter(clickedBuilding);
                })

            if (building.info.buildingIcon.length) {
                html.innerHtml(`
					<div class='building-labels_wrapper ${hideClickHere ? 'hide-click-here' : 'show-click-here'}' data-name="${building.name}" data-label="labelTower-${building.info.labelName}">
						<div class="building-label-bubble">${getBuildingLabel(labelBGColor)}</div>
						<div class="building-labels__icon" style="background: url(${building.info.buildingIcon})"></div>
					</div>
        		`);

            } else {
                html.innerHtml(`
					<div class='building-labels_wrapper ${hideClickHere ? 'hide-click-here' : 'show-click-here'}' data-name="${lot}" data-label="lot-${lot}">
						<div class="building-label-bubble">${getBuildingLabel(labelBGColor)}</div>
						<span>${lot}</span>
					</div>`
                );
            }

            html.addClass('active');

            if (colors.text) {
                html.DOMElement.querySelector('.building-labels_wrapper').style = `color: ${colors.text}`;
            }

            const labelWrapper = html.DOMElement.querySelector('.building-labels_wrapper');
            html.event('pointerenter', () => {
                if (settingsData.activeViewMode === ViewMode.Orbit) {
                    labelWrapper.classList.add('building__hovered');
                }
            });
            html.event('pointerleave', () => {
                labelWrapper.classList.remove('building__hovered');
            });
            // html.event('click', () => {
            //     if (settingsData.activeViewMode === ViewMode.Orbit) {
            //         enterToTheBuildingWithMaxAvailableApartments();
            //     }
            // });
        }

        // new BaseElement('span')
        // 	.addClass('building-cluster__lot')
        // 	.innerHtml(lot)
        // 	.appendTo(html);

        !Cluster.buildingLabelMode && new BaseElement()
            .addClass('building-cluster__icon', 'icon-cluster_buildings')
            .appendTo(html.DOMElement);

        super(html.DOMElement, threeJsVariables.perspectiveCamera);

        const lots = config.features.cluster?.lots || {};
        this.lot = lot;
        const minDistance = isMobile ? config.threejs.orbitControls.orbit.mobile.portrait.min : config.threejs.orbitControls.orbit.desktop.min;
        this.distance = Cluster.buildingLabelMode ? 0 : lots[lot] ? lots[lot].distance : minDistance * 1.25;
        !Cluster.buildingLabelMode && this.element.addEventListener('click', this.fly.bind(this));
        this.name = 'cluster_' + this.lot;

        if (!Cluster.buildingLabelMode) {
            // this.countElement = new BaseElement('span')
            //     .addClass('building-cluster__count')
            //     .innerHtml(0)
            //     .appendTo(html.DOMElement);

            this.lotElement = new BaseElement('span')
                .addClass('building-cluster__lot')
                .innerHtml(this.lot)
                .appendTo(html.DOMElement);
        }

        // threeJsVariables.composer.addUpdatableElement(this);
        Cluster.scope.push(this);
    }

    updateCount() {
        if (Cluster.buildingLabelMode)
            return;

        //this.countElement.innerHtml(this.children.length);
    }

    fly() {
        const controls = threeJsVariables.orbitControls;
        const destination = new Vector3(this.position.x, controls.target.y, this.position.z);
        const direction = new Vector3().subVectors(this.camera.position, this.position).normalize().setLength(this.distance * 0.9).add(destination);
        
        const openedClusters = Cluster.scope.filter(scope => scope.visible === false);
        openedClusters.forEach(scope => scope.merge());

        const mixer = GSAPAnimationMixer.registerNewMixer('cluster');
        mixer.addCallback('onComplete',() => this.split());

        const target = gsap.to(controls.target, {
            x: destination.x,
            y: destination.y,
            z: destination.z,
            duration: GSAPAnimationMixer.duration,
            ease: GSAPAnimationMixer.ease
        });

        const position = gsap.to(this.camera.position, {
            x: direction.x,
            y: direction.y,
            z: direction.z,
            duration: GSAPAnimationMixer.duration,
            ease: GSAPAnimationMixer.ease
        });

        mixer.addAnimation(target);
        mixer.addAnimation(position);

        mixer.play();
    }

    static hide() {
        Cluster.scope.forEach(scope => {
            scope.update = false;
            scope.visible = false;
        });
    }

    static show() {
        Cluster.scope.forEach(scope => {
            scope.update = true;
        });
    }
    
    static update() {
        for (let i = 0; i < Cluster.scope.length; i++) {
            Cluster.scope[i].tick();
        }
    }

}
